import { useState, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Mail, ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabase';
import { PageContainer } from '../components/layout/PageContainer';
import { Logo } from '../components/common/Logo';
import { Input } from '../components/common/Input';
import { Button } from '../components/common/Button';
import { AUTH_REDIRECT_KEY, safeRedirect } from '../utils/redirect';
import { isStandalone } from '../utils/standalone';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const OTP_LENGTH = 6;

const standalone = isStandalone();

export function LoginPage() {
  const [searchParams] = useSearchParams();
  const emailParam = searchParams.get('email');
  const initialEmail = emailParam ? decodeURIComponent(emailParam) : '';
  const [email, setEmail] = useState(initialEmail);

  // Persist the destination so it survives the magic-link round trip, which
  // may finish in a different tab than the one that asked for the link.
  // Sanitised on the way in as well as on the way out, so a hostile ?redirect=
  // is never even written to storage.
  const redirectParam = safeRedirect(searchParams.get('redirect'));
  if (redirectParam) {
    localStorage.setItem(AUTH_REDIRECT_KEY, redirectParam);
  } else {
    localStorage.removeItem(AUTH_REDIRECT_KEY);
  }
  /**
   * Why this person is looking at a login form they did not ask for.
   *
   * Since the canonical flip, every ranking email links to btvision.app. Someone
   * with the app installed on the old origin taps one, iOS opens it in Safari
   * rather than their install, localStorage there is empty — and they are signed
   * out with no explanation. Two signals catch that:
   *
   *   ?moved=1        the old origin's migration banner sent them here
   *   ?redirect=…     they were bounced off a members-only page, i.e. they were
   *                   following a link meant for someone already signed in
   *
   * Join links are excluded deliberately: those are genuinely new players, and
   * announcing that the app "moved" would be noise about a domain they have
   * never seen.
   */
  const cameFromOldOrigin =
    searchParams.get('moved') === '1' ||
    (!!redirectParam && !redirectParam.includes('/join/'));

  const [status, setStatus] = useState<
    'idle' | 'loading' | 'google-loading' | 'otp-sent' | 'otp-verifying' | 'error'
  >('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { signInWithMagicLink, signInWithGoogle } = useAuth();

  const startResendCooldown = useCallback(() => {
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      setErrorMessage('Por favor, insira um email válido.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.error?.message || 'Erro ao fazer login.');
        setStatus('error');
        return;
      }

      if (data.tokenHash && data.autoLogin) {
        // Instant login via OTP verification (staging auto-login only)
        const { error: otpError } = await supabase.auth.verifyOtp({
          token_hash: data.tokenHash,
          type: 'magiclink',
        });

        if (otpError) {
          // Fall back to sending OTP email if instant verification fails
          console.warn('[Login] verifyOtp failed, falling back to OTP email:', otpError.message);
          const { error } = await signInWithMagicLink(email);
          if (error) {
            setErrorMessage(error.message);
            setStatus('error');
          } else {
            setOtpDigits(Array(OTP_LENGTH).fill(''));
            setStatus('otp-sent');
            startResendCooldown();
            setTimeout(() => inputRefs.current[0]?.focus(), 100);
          }
          return;
        }

        // Session established — onAuthStateChange will handle redirect
        return;
      }

      // Existing user — send OTP email
      const { error } = await signInWithMagicLink(email);
      if (error) {
        setErrorMessage(error.message);
        setStatus('error');
      } else {
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        setStatus('otp-sent');
        startResendCooldown();
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }
    } catch {
      setErrorMessage('Erro de conexão. Tente novamente.');
      setStatus('error');
    }
  };

  const verifyOtp = async (code: string) => {
    setStatus('otp-verifying');
    setErrorMessage('');

    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: code,
        type: 'email',
      });

      if (error) {
        setErrorMessage('Código inválido ou expirado. Tente novamente.');
        setStatus('otp-sent');
        // Clear and refocus
        setOtpDigits(Array(OTP_LENGTH).fill(''));
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      }
      // Success: onAuthStateChange in AuthContext handles the redirect
    } catch {
      setErrorMessage('Erro de conexão. Tente novamente.');
      setStatus('otp-sent');
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);

    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    setErrorMessage('');

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits filled
    if (digit && index === OTP_LENGTH - 1) {
      const code = newDigits.join('');
      if (code.length === OTP_LENGTH) {
        verifyOtp(code);
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;

    const newDigits = [...otpDigits];
    for (let i = 0; i < pasted.length; i++) {
      newDigits[i] = pasted[i];
    }
    setOtpDigits(newDigits);

    if (pasted.length === OTP_LENGTH) {
      verifyOtp(pasted);
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setErrorMessage('');

    const { error } = await signInWithMagicLink(email);
    if (error) {
      setErrorMessage(error.message);
    } else {
      startResendCooldown();
      setOtpDigits(Array(OTP_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  };

  const handleGoogleSignIn = async () => {
    setStatus('google-loading');
    setErrorMessage('');

    try {
      await signInWithGoogle();
      // Redirect happens automatically via Supabase OAuth flow
    } catch {
      setErrorMessage('Erro ao conectar com Google. Tente novamente.');
      setStatus('error');
    }
  };

  return (
    <PageContainer className="flex items-center justify-center min-h-screen px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Logo size="lg" className="mb-2" />
          <p className="text-white/60">Plataforma de rankings de beach tennis</p>
        </div>

        {/* Card */}
        <div className="glass-card rounded-3xl p-6 sm:p-8">
          {status === 'otp-sent' || status === 'otp-verifying' ? (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-center py-2"
            >
              <h2 className="text-xl font-semibold text-white mb-2">
                Digite o código
              </h2>
              <p className="text-white/60 mb-6">
                Enviamos um código de 6 dígitos para{' '}
                <span className="text-turquoise">{email}</span>
              </p>

              {/* OTP Input */}
              <div className="flex justify-center gap-2 sm:gap-3 mb-4" onPaste={handleOtpPaste}>
                {otpDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(i, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                    disabled={status === 'otp-verifying'}
                    className={`
                      w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold
                      bg-white/5 border rounded-xl text-white
                      transition-all duration-200 outline-none
                      ${errorMessage ? 'border-coral' : 'border-white/10 focus:border-turquoise/50'}
                      disabled:opacity-50
                    `}
                  />
                ))}
              </div>

              {errorMessage && (
                <p className="text-sm text-coral mb-4">{errorMessage}</p>
              )}

              {status === 'otp-verifying' && (
                <p className="text-white/60 text-sm mb-4">Verificando...</p>
              )}

              <div className="bg-warning-orange/15 border border-warning-orange/30 rounded-xl px-4 py-3 mb-4">
                <p className="text-warning-orange text-sm font-medium">
                  Não encontrou? Verifique sua caixa de spam!
                </p>
              </div>

              <button
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className="text-sm text-turquoise hover:underline disabled:text-white/30 disabled:no-underline"
              >
                {resendCooldown > 0
                  ? `Reenviar código em ${resendCooldown}s`
                  : 'Reenviar código'}
              </button>

              <div className="mt-4">
                <button
                  onClick={() => {
                    setStatus('idle');
                    setErrorMessage('');
                    setOtpDigits(Array(OTP_LENGTH).fill(''));
                  }}
                  className="text-sm text-white/40 hover:text-white/60 inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Voltar
                </button>
              </div>
            </motion.div>
          ) : (
            <>
              {cameFromOldOrigin && (
                <div className="mb-5 rounded-xl border border-turquoise/30 bg-turquoise/10 px-4 py-3">
                  <p className="text-sm font-medium text-white">
                    O BT Vision mudou de endereço
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-white/60">
                    Agora estamos em <span className="text-turquoise">btvision.app</span>.
                    Entre com seu email — seus rankings, rodadas e histórico continuam
                    todos na sua conta.
                  </p>
                </div>
              )}

              <div className="space-y-5">
                {!standalone && (
                  <>
                    <Button
                      fullWidth
                      onClick={handleGoogleSignIn}
                      isLoading={status === 'google-loading'}
                      disabled={status === 'loading'}
                      variant="secondary"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                      <span>Continuar com Google</span>
                    </Button>

                    <div className="flex items-center gap-3 my-2">
                      <div className="flex-1 h-px bg-white/10" />
                      <span className="text-white/40 text-xs">ou</span>
                      <div className="flex-1 h-px bg-white/10" />
                    </div>
                  </>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (status === 'error') setStatus('idle');
                    }}
                    placeholder="seu@email.com"
                    error={status === 'error' ? errorMessage : undefined}
                    disabled={status === 'loading' || status === 'google-loading'}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    isLoading={status === 'loading'}
                    disabled={status === 'google-loading'}
                  >
                    <Mail className="w-5 h-5" />
                    <span>Continuar com email</span>
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </form>
              </div>

              <p className="mt-6 text-center text-xs text-white/40">
                Ao continuar, voce concorda com nossos{' '}
                <Link to="/terms" className="text-turquoise hover:underline">Termos de Uso</Link>
                {' '}e{' '}
                <Link to="/privacy" className="text-turquoise hover:underline">Politica de Privacidade</Link>.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-white/30">
          © 2026 BT Vision • btvision.app
        </p>
      </motion.div>
    </PageContainer>
  );
}
